"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";

declare global {
  interface Window {
    turnstile?: {
      render: (el: string | HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
    onTurnstileReady?: () => void;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * Cloudflare Turnstile wrapper. Public API is unchanged (`onToken`,
 * `action`); what's new is the Round 10 failure state: when the
 * challenge errors out (VPN, old browser, ad-blocker eating the
 * script) the widget names the real causes, offers an in-place retry
 * as the single action, and prints the error code in quiet gray for
 * support - instead of leaving the submit button silently disabled.
 */
export default function TurnstileWidget({
  onToken,
  action,
}: {
  onToken: (token: string | null) => void;
  action?: string;
}) {
  const t = useT();
  const id = useId().replace(/:/g, "-");
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  // Error code shown in the support footer ("turnstile_110200",
  // "turnstile_script_blocked", ...). null = challenge healthy.
  const [failedCode, setFailedCode] = useState<string | null>(null);
  // Bumped by the retry button when the script itself never loaded
  // (ad-blocker case) so the mount effect runs again and re-injects.
  const [attempt, setAttempt] = useState(0);

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || typeof window === "undefined") return;

    const mount = () => {
      const el = document.getElementById(id);
      if (!el || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        action,
        theme: "light",
        callback: (token: string) => {
          setFailedCode(null);
          onTokenRef.current(token);
        },
        "error-callback": (code?: string) => {
          setFailedCode(code ? `turnstile_${code}` : "turnstile_error");
          onTokenRef.current(null);
          // Tell Turnstile we handled the error so it doesn't spam
          // its own retry loop underneath our card.
          return true;
        },
        "expired-callback": () => onTokenRef.current(null),
      });
    };

    if (window.turnstile) {
      mount();
    } else if (!document.querySelector(`script[src^="${SCRIPT_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = mount;
      // Script never arrived: classic ad-blocker / firewall symptom.
      // Remove the dead tag so a retry can re-inject a fresh one.
      s.onerror = () => {
        s.remove();
        setFailedCode("turnstile_script_blocked");
        onTokenRef.current(null);
      };
      document.head.appendChild(s);
    } else {
      const iv = setInterval(() => {
        if (window.turnstile) {
          clearInterval(iv);
          mount();
        }
      }, 80);
      return () => clearInterval(iv);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, id, action, attempt]);

  const handleRetry = () => {
    setFailedCode(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        return;
      } catch { /* fall through to a full remount */ }
    }
    // No live widget (script was blocked or render failed) - re-run
    // the mount effect from scratch.
    setAttempt((a) => a + 1);
  };

  if (!siteKey) return null;
  return (
    <div>
      {/* The CF iframe container stays mounted through a failure so
          reset() can revive it; we just hide it under the card. */}
      <div id={id} className={failedCode ? "hidden" : "flex justify-center"} />
      {failedCode && (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-bold text-gray-900">
              {t("turnstile.failed.title")}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              {t("turnstile.failed.body")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleRetry}
            className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            {t("turnstile.retry")}
          </button>
          <p className="text-[11px] text-gray-400">
            {t("turnstile.code")
              .replace("{code}", failedCode)
              .split("{support}")
              .map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <Link
                      href="/contact"
                      className="underline underline-offset-2 hover:text-gray-600"
                    >
                      {t("turnstile.support")}
                    </Link>
                  )}
                </span>
              ))}
          </p>
        </div>
      )}
    </div>
  );
}
